import React, { useEffect, useState } from 'react';
import { 
  Users, 
  Shield, 
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Calendar,
  Mail,
  User,
  BookOpen,
  X,
  Eye,
  Edit2,
  Trash2,
  Save
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import StatsCard from '@/components/admin/StatsCard';
import Modal from '@/components/admin/Modal';
import adminEnrollmentsService from '@/services/adminEnrollments.service';
import { authService } from '@/services';
import type { EnrollmentDetails, SearchFilters, EnrollmentsStats } from '@/services/adminEnrollments.service';

// Helper function to get display name from first_name/last_name
const getEnrollmentUserName = (enrollment: EnrollmentDetails): string => {
  if (enrollment.user_first_name || enrollment.user_last_name) {
    return `${enrollment.user_first_name || ''} ${enrollment.user_last_name || ''}`.trim();
  }
  return enrollment.user_username;
};

export default function AdminEnrollments() {
  const [user, setUser] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentDetails[]>([]);
  const [stats, setStats] = useState<EnrollmentsStats>({
    total_enrollments: 0,
    active_enrollments: 0,
    completed_enrollments: 0,
    total_revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<EnrollmentDetails | null>(null);
  const [editForm, setEditForm] = useState<Partial<EnrollmentDetails>>({});
  const [processing, setProcessing] = useState(false);
  
  // Search state
  const [searchMode, setSearchMode] = useState<'simple' | 'advanced'>('simple');
  const [simpleSearch, setSimpleSearch] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    user_id: '',
    enrollment_id: '',
    username: '',
    email: '',
    product_id: '',
    status: ''
  });

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [enrollmentsData, statsData] = await Promise.all([
        adminEnrollmentsService.getAllEnrollments(),
        adminEnrollmentsService.getEnrollmentsStats()
      ]);
      
      if (!enrollmentsData.success) {
        throw new Error(enrollmentsData.error || 'Failed to fetch enrollments');
      }
      if (!statsData.success) {
        throw new Error(statsData.error || 'Failed to fetch stats');
      }
      
      setEnrollments(enrollmentsData.data || []);
      setStats(statsData.data || {
        total_enrollments: 0,
        active_enrollments: 0,
        completed_enrollments: 0,
        total_revenue: 0
      });
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSimpleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!simpleSearch.trim()) {
      fetchInitialData();
      return;
    }

    setSearching(true);
    setError(null);
    try {
      const response = await adminEnrollmentsService.searchEnrollments({
        search_query: simpleSearch.trim()
      });
      
      if (!response.success) {
        throw new Error(response.error || 'Search failed');
      }
      
      setEnrollments(response.data || []);
    } catch (error: any) {
      console.error('Error searching:', error);
      setError(error.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleAdvancedSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if any filter is set
    const hasFilters = Object.values(filters).some(value => value && value.trim() !== '');
    if (!hasFilters) {
      fetchInitialData();
      return;
    }

    setSearching(true);
    setError(null);
    try {
      const response = await adminEnrollmentsService.searchEnrollments(filters);
      
      if (!response.success) {
        throw new Error(response.error || 'Search failed');
      }
      
      setEnrollments(response.data || []);
    } catch (error: any) {
      console.error('Error searching:', error);
      setError(error.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSimpleSearch('');
    setFilters({
      user_id: '',
      enrollment_id: '',
      username: '',
      email: '',
      product_id: '',
      status: ''
    });
    fetchInitialData();
  };

  const handleViewEnrollment = (enrollment: EnrollmentDetails) => {
    setSelectedEnrollment(enrollment);
    setViewModalOpen(true);
  };

  const handleEditEnrollment = (enrollment: EnrollmentDetails) => {
    setSelectedEnrollment(enrollment);
    setEditForm({
      status: enrollment.status,
      progress_percentage: enrollment.progress_percentage,
      certificate_issued: enrollment.certificate_issued
    });
    setEditModalOpen(true);
  };

  const handleDeleteClick = (enrollment: EnrollmentDetails) => {
    setSelectedEnrollment(enrollment);
    setDeleteModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedEnrollment) return;

    setProcessing(true);
    setError(null);
    try {
      const response = await adminEnrollmentsService.updateEnrollment(
        selectedEnrollment.id,
        editForm
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update enrollment');
      }

      setSuccessMessage('Enrollment updated successfully!');
      setEditModalOpen(false);
      fetchInitialData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error updating enrollment:', error);
      setError(error.message || 'Failed to update enrollment');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedEnrollment) return;

    setProcessing(true);
    setError(null);
    try {
      const response = await adminEnrollmentsService.deleteEnrollment(selectedEnrollment.id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete enrollment');
      }

      setSuccessMessage('Enrollment deleted successfully!');
      setDeleteModalOpen(false);
      fetchInitialData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error deleting enrollment:', error);
      setError(error.message || 'Failed to delete enrollment');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-500/20 border-green-500/30 text-green-400',
      completed: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
      expired: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
      cancelled: 'bg-red-500/20 border-red-500/30 text-red-400',
      refunded: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status as keyof typeof styles] || styles.active}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <AdminLayout>
      <section className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 py-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                Enrollments Management
              </h1>
              <p className="text-gray-400">View, manage, and track all course and bundle enrollments</p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <Shield className="w-4 h-4" />
              <span>Admin Access</span>
            </div>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="bg-green-500/20 border border-green-500/30 text-green-400 px-6 py-4 rounded-lg flex items-center space-x-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>{successMessage}</span>
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-6 py-4 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <XCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatsCard
              label="Total Enrollments"
              value={stats.total_enrollments}
              icon={Users}
              iconColor="text-purple-400"
            />
            <StatsCard
              label="Active Enrollments"
              value={stats.active_enrollments}
              icon={CheckCircle2}
              iconColor="text-green-400"
            />
            <StatsCard
              label="Completed Courses"
              value={stats.completed_enrollments}
              icon={BookOpen}
              iconColor="text-blue-400"
            />
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="w-8 h-8 text-green-400" />
                <span className="text-3xl font-bold text-white">{formatCurrency(stats.total_revenue)}</span>
              </div>
              <h3 className="text-gray-400 text-sm">Total Revenue</h3>
            </div>
          </div>

          {/* Search Section */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                <Search className="w-6 h-6 text-purple-400" />
                <span>Search Enrollments</span>
              </h2>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                    showFilters 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-white/10 text-gray-400 hover:bg-white/20'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span>Advanced Filters</span>
                </button>
                <button
                  onClick={clearSearch}
                  className="px-4 py-2 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Simple Search */}
            {!showFilters && (
              <form onSubmit={handleSimpleSearch} className="flex space-x-4">
                <input
                  type="text"
                  value={simpleSearch}
                  onChange={(e) => setSimpleSearch(e.target.value)}
                  placeholder="Search by user name, email, or product title..."
                  className="flex-1 px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={searching}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </form>
            )}

            {/* Advanced Search */}
            {showFilters && (
              <form onSubmit={handleAdvancedSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      User ID
                    </label>
                    <input
                      type="text"
                      value={filters.user_id}
                      onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                      placeholder="Enter user ID"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <BookOpen className="w-4 h-4 inline mr-2" />
                      Enrollment ID
                    </label>
                    <input
                      type="text"
                      value={filters.enrollment_id}
                      onChange={(e) => setFilters({ ...filters, enrollment_id: e.target.value })}
                      placeholder="Enter enrollment ID"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Username
                    </label>
                    <input
                      type="text"
                      value={filters.username}
                      onChange={(e) => setFilters({ ...filters, username: e.target.value })}
                      placeholder="Enter username"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email
                    </label>
                    <input
                      type="text"
                      value={filters.email}
                      onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                      placeholder="Enter email"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <BookOpen className="w-4 h-4 inline mr-2" />
                      Product ID
                    </label>
                    <input
                      type="text"
                      value={filters.product_id}
                      onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
                      placeholder="Enter product ID"
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      <CheckCircle2 className="w-4 h-4 inline mr-2" />
                      Status
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="expired">Expired</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={searching}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </form>
            )}
          </div>

          {/* Enrollments Table */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                <Users className="w-6 h-6 text-purple-400" />
                <span>Enrollments ({enrollments.length})</span>
              </h2>
            </div>

            {loading ? (
              <div className="p-12 text-center text-gray-400">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
                <p className="mt-4">Loading enrollments...</p>
              </div>
            ) : enrollments.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <p className="text-xl">No enrollments found</p>
                <p className="text-sm mt-2">Try adjusting your search criteria</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Progress</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Enrolled</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {enrollments.map((enrollment) => (
                      <tr key={enrollment.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-white font-medium">{getEnrollmentUserName(enrollment)}</div>
                            <div className="text-sm text-gray-400">{enrollment.user_email}</div>
                            <div className="text-xs text-gray-500 font-mono">@{enrollment.user_username}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-white font-medium">{enrollment.product_title}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              <span className="px-2 py-1 bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded">
                                {enrollment.product_type.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(enrollment.status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-gray-700 rounded-full h-2 max-w-[100px]">
                              <div 
                                className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                                style={{ width: `${enrollment.progress_percentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-400">{enrollment.progress_percentage}%</span>
                          </div>
                          {enrollment.certificate_issued && (
                            <div className="text-xs text-green-400 mt-1">✓ Certificate Issued</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-white font-semibold">{formatCurrency(enrollment.purchase_price)}</div>
                          <div className="text-xs text-gray-500">{enrollment.currency}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-400">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(enrollment.enrolled_at)}
                          </div>
                          {enrollment.last_accessed_at && (
                            <div className="text-xs text-gray-500 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              Last: {formatDate(enrollment.last_accessed_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewEnrollment(enrollment)}
                              className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditEnrollment(enrollment)}
                              className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors"
                              title="Edit Enrollment"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(enrollment)}
                              className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                              title="Delete Enrollment"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* View Details Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="Enrollment Details"
      >
        {selectedEnrollment && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2">User Information</h3>
                <div className="space-y-2">
                  <p className="text-white"><span className="text-gray-400">Name:</span> {getEnrollmentUserName(selectedEnrollment)}</p>
                  <p className="text-white"><span className="text-gray-400">Username:</span> @{selectedEnrollment.user_username}</p>
                  <p className="text-white"><span className="text-gray-400">Email:</span> {selectedEnrollment.user_email}</p>
                  <p className="text-white"><span className="text-gray-400">User ID:</span> {selectedEnrollment.user_id}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Product Information</h3>
                <div className="space-y-2">
                  <p className="text-white"><span className="text-gray-400">Title:</span> {selectedEnrollment.product_title}</p>
                  <p className="text-white"><span className="text-gray-400">Type:</span> {selectedEnrollment.product_type.toUpperCase()}</p>
                  <p className="text-white"><span className="text-gray-400">Product ID:</span> {selectedEnrollment.product_id}</p>
                  <p className="text-white"><span className="text-gray-400">Price:</span> {formatCurrency(selectedEnrollment.purchase_price)}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Enrollment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <p className="text-white"><span className="text-gray-400">Enrollment ID:</span> {selectedEnrollment.id}</p>
                <p className="text-white"><span className="text-gray-400">Status:</span> {getStatusBadge(selectedEnrollment.status)}</p>
                <p className="text-white"><span className="text-gray-400">Progress:</span> {selectedEnrollment.progress_percentage}%</p>
                <p className="text-white"><span className="text-gray-400">Certificate:</span> {selectedEnrollment.certificate_issued ? '✓ Issued' : '✗ Not Issued'}</p>
                <p className="text-white"><span className="text-gray-400">Enrolled:</span> {formatDate(selectedEnrollment.enrolled_at)}</p>
                {selectedEnrollment.completed_at && (
                  <p className="text-white"><span className="text-gray-400">Completed:</span> {formatDate(selectedEnrollment.completed_at)}</p>
                )}
                {selectedEnrollment.last_accessed_at && (
                  <p className="text-white"><span className="text-gray-400">Last Accessed:</span> {formatDate(selectedEnrollment.last_accessed_at)}</p>
                )}
                {selectedEnrollment.access_expires_at && (
                  <p className="text-white"><span className="text-gray-400">Expires:</span> {formatDate(selectedEnrollment.access_expires_at)}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Enrollment"
      >
        {selectedEnrollment && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
              <select
                value={editForm.status || ''}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Progress Percentage</label>
              <input
                type="number"
                min="0"
                max="100"
                value={editForm.progress_percentage || 0}
                onChange={(e) => setEditForm({ ...editForm, progress_percentage: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="certificate"
                checked={editForm.certificate_issued || false}
                onChange={(e) => setEditForm({ ...editForm, certificate_issued: e.target.checked })}
                className="w-4 h-4 rounded border-white/10 bg-black/40 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="certificate" className="text-white">Certificate Issued</label>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleSaveEdit}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{processing ? 'Saving...' : 'Save Changes'}</span>
              </button>
              <button
                onClick={() => setEditModalOpen(false)}
                disabled={processing}
                className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Deletion"
      >
        {selectedEnrollment && (
          <div className="space-y-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-400 mb-2">
                Are you sure you want to delete this enrollment? This action cannot be undone.
              </p>
              <div className="text-sm text-gray-400 space-y-1 mt-4">
                <p><span className="font-semibold">User:</span> {getEnrollmentUserName(selectedEnrollment)} ({selectedEnrollment.user_email})</p>
                <p><span className="font-semibold">Product:</span> {selectedEnrollment.product_title}</p>
                <p><span className="font-semibold">Enrollment ID:</span> {selectedEnrollment.id}</p>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleConfirmDelete}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-800 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>{processing ? 'Deleting...' : 'Delete Enrollment'}</span>
              </button>
              <button
                onClick={() => setDeleteModalOpen(false)}
                disabled={processing}
                className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}
